import React, { useState, useEffect } from "react";
import { Heart, Stethoscope, Bandage, Calendar, Users, FileText, Home, Phone } from "lucide-react";
import { contentAPI, servicesAPI } from "../services/api";
import { useLanguage } from "../hooks/useLanguage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const ICON_MAP = {
  Heart,
  Stethoscope,
  Bandage,
  Calendar,
  Users,
  FileText,
  Home,
  Phone,
};

const DEFAULT_SERVICES = [
  { icon: "Heart", title: "Grundpflege", description: "Körperpflege, Hilfe beim An-/Auskleiden, Mobilisation, Nahrungsaufnahme, Lagern und Betten", color: "var(--vortex-accent-primary)" },
  { icon: "Stethoscope", title: "Krankenpflege", description: "Verbandswechsel, Dekubitus Versorgung, Injektionen, Infusionen, Medikamentengabe, künstliche Ernährung, Katheterisierung, Tracheostoma", color: "var(--vortex-health)" },
  { icon: "Bandage", title: "Wundversorgung", description: "Modernes Wundmanagement, Behandlung chronischer Wunden, Wunddokumentation. Wir senken Behandlungskosten um mehr als die Hälfte.", color: "var(--vortex-accent-primary)" },
  { icon: "Calendar", title: "Urlaubs- und Verhinderungspflege", description: "Stundenweise Vertretung, auch nachts. Wir vertreten Sie, wenn Sie Verwandte besuchen, wegfahren oder krank sind.", color: "var(--vortex-health)" },
  { icon: "Users", title: "Betreuungsangebote", description: "24h Betreuung und stundenweise Vertretung. Kostengünstige Alternative zu Altenheim und Pflegediensten.", color: "var(--vortex-accent-primary)" },
  { icon: "FileText", title: "Pflegeberatung", description: "Pflegeberatung nach §37 Abs. 3 SGB XI. Individuelle Schulungen, Organisation und Finanzierung der Pflege.", color: "var(--vortex-health)" },
  { icon: "Home", title: "Hauswirtschaftliche Leistungen", description: "Einkaufen, Reinigung, Wäsche, Heizen, Mahlzeiten, Fensterputzen, Treppenreinigung.", color: "var(--vortex-accent-primary)" },
];

const ServicesPreview = () => {
  const currentLanguage = useLanguage();
  const [sectionData, setSectionData] = useState(null);
  const [apiServicesList, setApiServicesList] = useState([]);

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const sections = await contentAPI.getSections("home").catch(() => []);
        const servicesSection = sections.find(s => s.section_type === "services");
        if (servicesSection?.content_data) {
          setSectionData(servicesSection.content_data);
        }
      } catch (error) {
        console.error("Error fetching services section:", error);
      }
    };
    fetchSectionData();
  }, []);

  useEffect(() => {
    servicesAPI.getAll().then(setApiServicesList).catch(() => setApiServicesList([]));
  }, []);

  const getStringValue = (value, fallback = "") => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value[currentLanguage] || value['de'] || value[Object.keys(value)[0]] || fallback;
    }
    return String(value) || fallback;
  };

  const sectionTitle = sectionData ? getStringValue(sectionData.title, "Unsere Leistungen") : "Unsere Leistungen";
  const sectionDescription = sectionData ? getStringValue(sectionData.description, "Umfassende Pflege und Betreuung für ein selbstbestimmtes Leben zu Hause") : "Umfassende Pflege und Betreuung für ein selbstbestimmtes Leben zu Hause";
  const ctaText = sectionData ? getStringValue(sectionData.cta_text, "Haben Sie Fragen zu unseren Leistungen?") : "Haben Sie Fragen zu unseren Leistungen?";
  const ctaButton = sectionData?.cta_button || { label: "02133-21 84 111", href: "tel:+4921332184111" };

  // Build full list from API (apiServicesList) or section inline services or defaults
  const allServicesList = apiServicesList.length > 0 ? apiServicesList : (sectionData?.services || []);
  const services = allServicesList.length > 0
    ? allServicesList.map((service, index) => {
        const defaultItem = DEFAULT_SERVICES[index];
        const iconName = (service.icon || defaultItem?.icon || "Heart").trim();
        const IconComponent = ICON_MAP[iconName] || ICON_MAP[iconName?.replace(/\s/g, "")] || Heart;
        const color = service.color || defaultItem?.color || "var(--vortex-accent-primary)";
        return {
          id: service.id,
          icon: IconComponent,
          iconName,
          title: getStringValue(service.title, defaultItem?.title || "Service"),
          description: getStringValue(service.description, defaultItem?.description || ""),
          color,
          image_url: service.image_url || null,
        };
      })
    : DEFAULT_SERVICES.map((item, index) => ({
        id: index,
        icon: ICON_MAP[item.icon] || Heart,
        iconName: item.icon,
        title: item.title,
        description: item.description,
        color: item.color,
        image_url: null,
      }));

  const group1Ids = sectionData?.group1_service_ids || [];
  const group2Ids = sectionData?.group2_service_ids || [];
  const byId = Object.fromEntries(services.map((s) => [String(s.id), s]));
  const group1 = group1Ids.length > 0
    ? group1Ids.map((id) => byId[String(id)]).filter(Boolean)
    : services.slice(0, 3);
  const group2 = group2Ids.length > 0
    ? group2Ids.map((id) => byId[String(id)]).filter(Boolean)
    : services.slice(3, 5);

  return (
    <section id="services" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-[var(--vortex-bg)]">
      <div className="max-w-4xl mx-auto w-full min-w-0">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-[var(--vortex-text-primary)]">
            {sectionTitle}
          </h2>
          <p className="text-base sm:text-xl text-[var(--vortex-text-secondary)] max-w-3xl mx-auto leading-relaxed px-0 sm:px-2">
            {sectionDescription}
          </p>
        </div>

        {/* First tab group: 3 tabs (e.g. Krankenpflege, Haushaltshilfe, Urlaubs-/Verhinderungspflege) */}
        <Tabs defaultValue="0" className="w-full mb-8 sm:mb-12 rounded-xl sm:rounded-2xl border border-[var(--vortex-border)] overflow-hidden bg-white">
          <TabsList className="w-full h-auto p-2 flex flex-wrap gap-1 rounded-none rounded-t-xl sm:rounded-t-2xl border-b border-[var(--vortex-border)] bg-[var(--vortex-surface-alt)]/70 min-w-0">
            {group1.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <TabsTrigger
                  key={index}
                  value={String(index)}
                  className="group flex items-center gap-2 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-[var(--vortex-text-secondary)] bg-[var(--vortex-surface-alt)]/80 transition-all data-[state=active]:!bg-[#edd09d] data-[state=active]:text-[#6a5959] data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=active]:border-2 data-[state=active]:!border-[#edd09d] data-[state=active]:border-b-white data-[state=active]:-mb-px min-w-0 flex-1 sm:flex-initial basis-[calc(50%-4px)] sm:basis-auto whitespace-normal text-left"
                >
                  <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 opacity-70 group-data-[state=active]:!text-[#6a5959] group-data-[state=active]:opacity-100" style={{ color: service.color }} />
                  <span className="line-clamp-2 sm:line-clamp-none">{service.title}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          {group1.map((service, index) => (
            <TabsContent
              key={index}
              value={String(index)}
              className="mt-0 rounded-none rounded-b-xl sm:rounded-b-2xl border-0 bg-white p-4 sm:p-6 md:p-8 focus-visible:outline-none focus-visible:ring-0 min-w-0 overflow-hidden"
            >
              {service.image_url && (
                <div className="mb-6 rounded-xl overflow-hidden max-w-sm">
                  <img src={service.image_url} alt={service.title} className="w-full h-40 object-cover" />
                </div>
              )}
              <p className="text-base text-[var(--vortex-text-primary)] leading-relaxed">
                {service.description}
              </p>
            </TabsContent>
          ))}
        </Tabs>

        {/* Second tab group: 2 tabs (e.g. Wundversorgung, Pflegeberatung) */}
        {group2.length > 0 && (
        <Tabs defaultValue="0" className="w-full rounded-xl sm:rounded-2xl border border-[var(--vortex-border)] overflow-hidden bg-white">
          <TabsList className="w-full h-auto p-2 flex flex-wrap gap-1 rounded-none rounded-t-xl sm:rounded-t-2xl border-b border-[var(--vortex-border)] bg-[var(--vortex-surface-alt)]/70 min-w-0">
            {group2.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <TabsTrigger
                  key={`g2-${index}`}
                  value={String(index)}
                  className="group flex items-center gap-2 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-[var(--vortex-text-secondary)] bg-[var(--vortex-surface-alt)]/80 transition-all data-[state=active]:!bg-[#edd09d] data-[state=active]:text-[#6a5959] data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=active]:border-2 data-[state=active]:!border-[#edd09d] data-[state=active]:border-b-white data-[state=active]:-mb-px min-w-0 flex-1 sm:flex-initial basis-[calc(50%-4px)] sm:basis-auto whitespace-normal text-left"
                >
                  <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 opacity-70 group-data-[state=active]:!text-[#6a5959] group-data-[state=active]:opacity-100" style={{ color: service.color }} />
                  <span className="line-clamp-2 sm:line-clamp-none">{service.title}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          {group2.map((service, index) => (
            <TabsContent
              key={`g2-${index}`}
              value={String(index)}
              className="mt-0 rounded-none rounded-b-xl sm:rounded-b-2xl border-0 bg-white p-4 sm:p-6 md:p-8 focus-visible:outline-none focus-visible:ring-0 min-w-0 overflow-hidden"
            >
              {service.image_url && (
                <div className="mb-6 rounded-xl overflow-hidden max-w-sm">
                  <img src={service.image_url} alt={service.title} className="w-full h-40 object-cover" />
                </div>
              )}
              <p className="text-base text-[var(--vortex-text-primary)] leading-relaxed">
                {service.description}
              </p>
            </TabsContent>
          ))}
        </Tabs>
        )}

        {ctaText && (
          <div className="text-center mt-10 sm:mt-16">
            <p className="text-base sm:text-xl text-[var(--vortex-text-secondary)] mb-4 sm:mb-6 px-2">
              {ctaText}
            </p>
            {ctaButton && (
              <a
                href={ctaButton.href || "tel:+4921332184111"}
                className="btn-primary px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold inline-flex items-center gap-2 w-full max-w-sm sm:max-w-none sm:w-auto"
                aria-label={`Rufen Sie uns an: ${getStringValue(ctaButton.label)}`}
              >
                <Phone className="w-5 h-5 shrink-0" />
                {getStringValue(ctaButton.label)}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesPreview;
